/**
 * C0-ROTATE: buildings and stone blocks drawn at any camera angle from their own painted sprites.
 *
 * C0 paints each building once, for the first quarter turn, with a closed-form 2:1 projection of
 * footprint units and height (buildings.ts `P`). While it paints, the painter notes its structural
 * faces (walls, tops, roof slopes: `faceRec`). Those faces are a low-poly model that matches the
 * picture exactly, and the picture is a parallel projection of them, so each face's texture
 * coordinates are just its corners pushed through the same projection. Drawn from any other angle
 * through the camera's turned projection (IsoCamera.proj), each face keeps its own painted pixels:
 * at the painted angle the result is the sprite, pixel for pixel, and in between the faces turn
 * with the ground instead of standing still on it. A painter only draws the two front faces, so the
 * back of a building is the front again, turned half way round (buildings are painted the same
 * from every quarter: C0's quarter turns already showed exactly that).
 *
 * - Faces are grown by a pixel and a half so the painted outline round the silhouette stays on them.
 * - Decals painted on a wall (windows, doors, couplets) lie inside it and are dropped as faces; the
 *   wall's texture carries them.
 * - Pixels of the sprite that lie on no face (ridge ornaments, spires, curled eave tips, a banner on a
 *   pole) are kept as a small upright picture standing at the building's middle.
 * - Faces turned away from the camera are left out and the rest drawn back to front (a building is
 *   a stack of boxes and roofs, which that orders correctly). Level tops come from whichever view,
 *   painted or turned half way, is nearer the camera's angle.
 * - Depth against the figures: as the painted buildings, in vertical strips each sorted at the
 *   depth of the footprint's front edge under it, now at the camera's angle. A stone block is one
 *   piece sorted at its front corner.
 *
 * Nothing here touches the rules: the footprints, collision and heights stay in packages/shared.
 *
 * Port to client0 (lane C0-ROTATE-PORT): the pieces come from the props themselves (terrain.ts tags a building's strips
 * and each stone block with a `TurnedSource`), made the first time the turned camera sees them, under the frame's art
 * budget (drawBudget.ts; a building not made yet is drawn painted for that frame), so it works with the big maps'
 * per-chunk scenery (propStream.ts). The faces come from the painting the props already did (terrain.ts
 * `buildingLook` notes them, `paintedFaces`): nothing is painted twice. At the painted angle and pitch (not `warped`)
 * none of this runs: the painted props are drawn as before.
 */
import { toScreen, type IsoOrigin } from '@nine/shared';
import { CanvasSource, Matrix, Mesh, MeshGeometry, Texture } from 'pixi.js';
import type { IsoCamera } from '../camera';
import type { Face3 } from '../buildings';
import { canDraw, timed } from '../drawBudget';
import { gpuPrepare } from '../gpuPrepare';
import { canvas, ctx2d, resOf } from '../pixel';
import type { Sprite } from '../scenery';
import type { Prop } from '../terrain';
import { paintedFaces, type TurnedSource } from './kinds';

/** Art pixels a metre goes up on screen: heights are drawn unforeshortened (16√2). */
const PX_M = 16 * Math.SQRT2;
/** The painted view looks along −(1, 1, 1/√2) in metres: the 2:1 ground and upright heights. */
const VIEW_Z = Math.SQRT1_2;
/** How far each face is grown (tiles along it, art pixels up), so the outline round it stays on it. */
const GROW = 0.1;
const GROW_Z = 2;
/** Strip width (art pixels) buildings are cut into for depth sorting against the figures. */
const STRIP = 8;

type V3 = readonly [number, number, number];
type UV = readonly [number, number];

interface Face {
  /** Corners on the map (tiles) and height (art pixels). */
  readonly p: readonly V3[];
  readonly uv: readonly UV[];
  /** Outward normal, in metres. */
  readonly n: V3;
  /** Its centre in metres, for ordering. */
  readonly c: V3;
  /** Painted (0) or the painted view turned half way round (1). */
  readonly src: 0 | 1;
  readonly level: boolean;
}

/** What a frame draws of a piece: a vertical strip of it at the camera's angle. */
export interface TurnedStrip {
  readonly piece: Piece;
  /** Depth key comparable with entity depth (IsoCamera.depth), at the camera's exact angle. */
  d: number;
  /** World pixels (the turned view) it covers, at the camera's exact angle. */
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  /**
   * Triangles, TRI floats a corner: its ground point (world pixels at the cached angle step), its
   * height (pixels up the screen), u, v.
   */
  readonly tri: Float32Array;
  /** This frame's turn of the ground points from the step's angle to the exact one (an affine map). */
  m: Affine;
  /** Over the player and in front of them this frame: drawn see-through (renderer.ts). */
  peek: boolean;
  readonly base: { readonly d: number; readonly x0: number; readonly x1: number; readonly y0: number; readonly y1: number };
}

/**
 * Strips are made once per angle step (ANGLE_STEP degrees at rest, TURNING_STEP while turning) and kept for the last few steps a piece
 * was seen at; each frame they are moved to the exact angle: the turn about the map's middle is
 * the turn about the piece's own middle (within half a step: under a pixel for a building's size)
 * plus a shift, which is exact.
 */
const ANGLE_STEP = 1;
/** Floats per triangle corner in `TurnedStrip.tri`. */
const TRI = 5;
type Affine = [number, number, number, number, number, number];
/** The step while the camera is turning (faces and strip cuts from up to 3° off; the geometry is exact, see `place`). */
const TURNING_STEP = 6;
/** Angle steps kept per piece (dragging back and forth, resting after a turn). */
const STEPS_KEPT = 6;

interface Made {
  readonly strips: TurnedStrip[];
  /** The piece's middle on screen (world pixels) and its depth at the step's angle. */
  readonly cx: number;
  readonly cy: number;
  readonly cd: number;
  /** Footprint front corner depth at the step. */
  readonly frontD: number;
  /** The step's angle (degrees) and the pitch it was made for. */
  readonly deg: number;
  readonly pitch: number;
}

/** A building or a stone block. */
export interface Piece {
  readonly id: number;
  readonly tex: Texture;
  readonly faces: readonly Face[];
  /** Footprint on the map (tiles): x0, y0, x1, y1. */
  readonly foot: readonly [number, number, number, number];
  /** Cut into strips (buildings) or one piece (blocks). */
  readonly strips: boolean;
  /** Sprite pixels on no face, standing at the footprint's middle: world pixels of the painted view. */
  readonly rest: Prop | null;
  /** The footprint's front corner's depth at the camera's angle (where the upright rest sorts). */
  frontD: number;
  /** The camera angle the strips are placed for this frame, and them. */
  angle: number;
  made: TurnedStrip[];
  /** Strips per angle step (most recent last), and the step size the current ones were placed from. */
  steps: Map<number, Made>;
  stepUsed: number;
  /** The pitch the current strips are placed for. */
  pitch: number;
  /** How far it is faded for the player behind it (0..1), and whether it hides them this frame. */
  fade: number;
  hides: boolean;
}

const textures = new WeakMap<HTMLCanvasElement, Texture>();
function textureOf(img: HTMLCanvasElement): Texture {
  let t = textures.get(img);
  if (!t) {
    textures.set(
      img,
      (t = new Texture({ source: new CanvasSource({ resource: img, scaleMode: 'nearest', autoGenerateMipmaps: false }) })),
    );
    // Onto the GPU now (the piece is being made under the art budget), not on the frame it is first drawn.
    gpuPrepare.source?.(t.source);
  }
  return t;
}

const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const dot = (a: V3, b: V3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const metres = (p: V3): V3 => [p[0], p[1], p[2] / PX_M];
const norm = (a: V3): V3 => {
  const l = Math.hypot(a[0], a[1], a[2]) || 1;
  return [a[0] / l, a[1] / l, a[2] / l];
};

/** Distinct corners of a face (a hip roof's ends and a pyramid's slopes are triangles). */
function corners(pts: readonly V3[]): V3[] {
  const out: V3[] = [];
  for (const p of pts)
    if (!out.some((q) => Math.abs(q[0] - p[0]) < 1e-6 && Math.abs(q[1] - p[1]) < 1e-6 && Math.abs(q[2] - p[2]) < 1e-6))
      out.push(p);
  return out;
}

/** The face's normal in metres, pointing out of the building (up for roofs and tops). */
function normalOf(pts: readonly V3[], kind: Face3['kind'], centre: V3): V3 | null {
  const m = pts.map(metres);
  let n: V3 = [0, 0, 0];
  for (let k = 1; k + 1 < m.length; k++) {
    const c = cross(sub(m[k]!, m[0]!), sub(m[k + 1]!, m[0]!));
    n = [n[0] + c[0], n[1] + c[1], n[2] + c[2]];
  }
  if (Math.hypot(...n) < 1e-9) return null;
  n = norm(n);
  if (kind === 'wall') {
    // Out from the building's middle (a wall's own side: +x or +y for a front face).
    const mid = metres(centre);
    const fc = m.reduce<V3>((a, p) => [a[0] + p[0] / m.length, a[1] + p[1] / m.length, a[2] + p[2] / m.length], [0, 0, 0]);
    const out = sub(fc, mid);
    if (n[0] * out[0] + n[1] * out[1] < 0) n = [-n[0], -n[1], -n[2]];
  } else if (n[2] < 0) n = [-n[0], -n[1], -n[2]];
  return n;
}

/** A point on a face's plane in 2D (for containment): along two axes of the plane. */
function planeAxes(n: V3): [V3, V3] {
  const a: V3 = Math.abs(n[2]) > 0.9 ? [1, 0, 0] : norm(cross([0, 0, 1], n));
  return [a, cross(n, a)];
}

function inside2(poly: readonly (readonly [number, number])[], x: number, y: number): boolean {
  let on = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i]!;
    const [xj, yj] = poly[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) on = !on;
  }
  return on;
}

type Raw = { pts: V3[]; n: V3; kind: Face3['kind'] };

/** Drops faces lying inside another face of the same plane (decals the wall's texture carries). */
function withoutDecals(faces: Raw[]): Raw[] {
  const keep: Raw[] = [];
  const eps = 0.035;
  for (let i = 0; i < faces.length; i++) {
    const f = faces[i]!;
    const [a, b] = planeAxes(f.n);
    const fm = f.pts.map(metres);
    const off = dot(fm[0]!, f.n);
    let inner = false;
    for (let j = 0; j < faces.length && !inner; j++) {
      if (j === i) continue;
      const g = faces[j]!;
      if (dot(g.n, f.n) < 0.999) continue;
      const gm = g.pts.map(metres);
      if (Math.abs(dot(gm[0]!, g.n) - off) > eps) continue;
      const poly = gm.map((p) => [dot(p, a), dot(p, b)] as const);
      // Inside, edges allowed: shrink the test points a hair toward the face's middle.
      const cx = fm.reduce((s, p) => s + dot(p, a), 0) / fm.length;
      const cy = fm.reduce((s, p) => s + dot(p, b), 0) / fm.length;
      const pts = fm.map((p) => [dot(p, a) * 0.98 + cx * 0.02, dot(p, b) * 0.98 + cy * 0.02] as const);
      const area = (q: readonly (readonly [number, number])[]) =>
        Math.abs(q.reduce((s, p, k) => s + p[0] * q[(k + 1) % q.length]![1] - q[(k + 1) % q.length]![0] * p[1], 0));
      // The bigger face keeps it; of two the same, the first painted.
      const ga = area(poly);
      const fa = area(fm.map((p) => [dot(p, a), dot(p, b)] as const));
      if (ga < fa - 1e-6 || (Math.abs(ga - fa) < 1e-6 && j > i)) continue;
      if (pts.every(([x, y]) => inside2(poly, x, y) || poly.some(([px, py]) => Math.hypot(px - x, py - y) < 0.02)))
        inner = true;
    }
    if (!inner) keep.push(f);
  }
  return keep;
}

/** Grows a face in its plane, so the painted outline just outside it lands on it. */
function grown(f: Raw): V3[] {
  if (f.kind === 'wall') {
    // Along the wall and up: its two horizontal ends and its top and foot.
    const [p0, p1, , p3] = f.pts as unknown as [V3, V3, V3, V3];
    const along = norm([p1[0] - p0[0], p1[1] - p0[1], 0]);
    const z0 = Math.min(p0[2], p3[2]) - GROW_Z;
    const z1 = Math.max(p0[2], p3[2]) + GROW_Z;
    const a: V3 = [p0[0] - along[0] * GROW, p0[1] - along[1] * GROW, 0];
    const b: V3 = [p1[0] + along[0] * GROW, p1[1] + along[1] * GROW, 0];
    return [
      [a[0], a[1], z0],
      [b[0], b[1], z0],
      [b[0], b[1], z1],
      [a[0], a[1], z1],
    ];
  }
  const c = f.pts.reduce<V3>(
    (s, p) => [s[0] + p[0] / f.pts.length, s[1] + p[1] / f.pts.length, s[2] + p[2] / f.pts.length],
    [0, 0, 0],
  );
  return f.pts.map((p) => {
    const d = sub(metres(p), metres(c));
    const l = Math.hypot(d[0], d[1], d[2]) || 1;
    const k = GROW / l;
    return [p[0] + d[0] * k, p[1] + d[1] * k, p[2] + d[2] * PX_M * k] as V3;
  });
}

let nextId = 1;
/** Rest pictures by painting and way round (0 as painted, 1 swapped): the same for every building painted alike. */
const rests = new WeakMap<HTMLCanvasElement, Map<number, HTMLCanvasElement | null>>();
/** Faces of a stone block `h` art pixels tall, in tile units (a box of the tile). */
const boxes = new Map<number, Face3[]>();
function boxOf(h: number): Face3[] {
  let f = boxes.get(h);
  if (!f)
    boxes.set(
      h,
      (f = [
        { kind: 'wall', pts: [[0, 1, 0], [1, 1, 0], [1, 1, h], [0, 1, h]] },
        { kind: 'wall', pts: [[1, 0, 0], [1, 1, 0], [1, 1, h], [1, 0, h]] },
        { kind: 'top', pts: [[0, 1, h], [1, 1, h], [1, 0, h], [0, 0, h]] },
      ]),
    );
  return f;
}

/**
 * Faces of a painted sprite, as they lie on the map. `local` → map turns the painter's footprint units
 * (a gate painted along x but standing along y is its mirror image: x and y swap); `P` is the sprite's
 * own projection (the anchor at the footprint's north corner).
 */
function makePiece(
  raw: readonly Face3[],
  sp: Sprite,
  toMap: (x: number, y: number) => [number, number],
  swap: boolean,
  foot: readonly [number, number, number, number],
  origin: IsoOrigin,
  strips: boolean,
  rest: boolean,
): Piece | null {
  const W = sp.img.width;
  const H = sp.img.height;
  const centre: V3 = [(foot[0] + foot[2]) / 2, (foot[1] + foot[3]) / 2, 0];
  const onMap = (p: V3): V3 => {
    const [x, y] = swap ? [p[1], p[0]] : [p[0], p[1]];
    const [mx, my] = toMap(x, y);
    return [mx, my, p[2]];
  };
  // Texture coordinates: the painted projection, from the footprint's north corner (the anchor).
  const [ox, oy] = [foot[0], foot[1]];
  const uvOf = (p: V3): UV => [(sp.ax + (p[0] - ox - (p[1] - oy)) * 16) / W, (sp.ay + (p[0] - ox + (p[1] - oy)) * 8 - p[2]) / H];
  const v0: V3 = norm([1, 1, VIEW_Z]);
  let faces: Raw[] = [];
  for (const f of raw) {
    const pts = corners(f.pts.map(onMap));
    if (pts.length < 3) continue;
    const n = normalOf(pts, f.kind, centre);
    if (!n) continue;
    // Only the faces the painted view sees carry its pixels.
    if (dot(n, v0) <= 1e-3) continue;
    faces.push({ pts, n, kind: f.kind });
  }
  faces = withoutDecals(faces);
  if (!faces.length) return null;
  const out: Face[] = [];
  const mirror = (p: V3): V3 => [2 * centre[0] - p[0], 2 * centre[1] - p[1], p[2]];
  for (const f of faces) {
    const g = grown(f);
    const uv = g.map(uvOf);
    const level = Math.abs(f.n[2]) > 0.999;
    const c = g.reduce<V3>((s, p) => {
      const m = metres(p);
      return [s[0] + m[0] / g.length, s[1] + m[1] / g.length, s[2] + m[2] / g.length];
    }, [0, 0, 0]);
    out.push({ p: g, uv, n: f.n, c, src: 0, level });
    // The back, as the front turned half way round the footprint's middle.
    const gm = g.map(mirror);
    out.push({
      p: gm,
      uv,
      n: [-f.n[0], -f.n[1], f.n[2]],
      c: [2 * centre[0] - c[0], 2 * centre[1] - c[1], c[2]],
      src: 1,
      level,
    });
  }
  // The sprite's pixels on no face: an upright picture at the middle (made once per painting and way round).
  let restPic: Piece['rest'] = null;
  const restKey = swap ? 1 : 0;
  let restBy = rests.get(sp.img);
  if (!restBy) rests.set(sp.img, (restBy = new Map()));
  let restImg = rest ? restBy.get(restKey) : null;
  if (rest && restImg === undefined) {
    restImg = null;
    const mask = canvas(W, H);
    const mg = ctx2d(mask);
    mg.fillStyle = '#fff';
    for (const f of out)
      if (f.src === 0) {
        mg.beginPath();
        f.uv.forEach(([u, v], k) => (k ? mg.lineTo(u * W, v * H) : mg.moveTo(u * W, v * H)));
        mg.closePath();
        mg.fill();
      }
    const m = mg.getImageData(0, 0, W, H).data;
    const src = ctx2d(sp.img).getImageData(0, 0, W, H);
    const s = src.data;
    let any = 0;
    for (let k = 0; k < W * H; k++) {
      if (m[k * 4 + 3]! >= 128) s[k * 4 + 3] = 0;
      else if (s[k * 4 + 3]! > 0) any++;
    }
    if (any > 0) {
      restImg = canvas(W, H);
      ctx2d(restImg).putImageData(src, 0, 0);
    }
    restBy.set(restKey, restImg);
  }
  if (restImg) {
    const [sx, sy] = toScreen(foot[0], foot[1], origin);
    restPic = {
      img: restImg,
      x: Math.round(sx - sp.ax),
      y: Math.round(sy - sp.ay),
      d: 0,
      foot: toScreen(centre[0], centre[1], origin),
    };
  }
  return {
    id: nextId++,
    tex: textureOf(sp.img),
    faces: out,
    foot,
    strips,
    rest: restPic,
    frontD: 0,
    angle: Number.NaN,
    made: [],
    steps: new Map(),
    stepUsed: 0,
    pitch: 30,
    fade: 0,
    hides: false,
  };
}

/** Clips a convex polygon (x, y, u, v per corner) to x0 ≤ x ≤ x1. */
function clip(poly: number[][], x0: number, x1: number): number[][] {
  const side = (inP: (p: number[]) => boolean, xc: number, pts: number[][]) => {
    const out: number[][] = [];
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i]!;
      const b = pts[(i + 1) % pts.length]!;
      const ia = inP(a);
      const ib = inP(b);
      if (ia) out.push(a);
      if (ia !== ib) {
        const t = (xc - a[0]!) / (b[0]! - a[0]!);
        out.push(a.map((v, k) => v + (b[k]! - v) * t));
      }
    }
    return out;
  };
  let p = side((q) => q[0]! >= x0, x0, poly);
  if (p.length) p = side((q) => q[0]! <= x1, x1, p);
  return p;
}

/**
 * The turned pieces seen this frame, and how they are drawn: each made once from its props' tag (`take`), its strips
 * made again only when the camera's angle step or pitch changes.
 */
export class TurnedScenery {
  /** Pieces by their props' tag; null: nothing to turn (no faces noted), the props stay painted. */
  private pieces = new WeakMap<TurnedSource, Piece | null>();
  /** The pieces whose props the frame's scenery query found. */
  private readonly seen = new Set<Piece>();
  private readonly meshes: Mesh[] = [];
  private used = 0;

  /** The camera this frame, and the angle step its strips are made at. */
  private iso: IsoCamera | null = null;
  private step = ANGLE_STEP;

  /** Starts a frame's scenery (before the props are gathered), while the view is turned or tilted. */
  frame(iso: IsoCamera): void {
    this.seen.clear();
    this.iso = iso;
    const c = Math.cos(iso.spin);
    const s = Math.sin(iso.spin);
    // Toward the camera on the map (the painted view's, turned back by the spin).
    this.toward = [c + s, c - s];
    // Turning: coarser steps (fewer new strips to make); at rest, the fine step.
    this.step = iso.angle !== this.lastAngle || iso.pitch !== this.lastPitch ? TURNING_STEP : ANGLE_STEP;
    this.lastAngle = iso.angle;
    this.lastPitch = iso.pitch;
  }

  /**
   * A prop the frame would draw, while the view is turned or tilted: true if its piece draws it turned instead (the
   * prop is then left out), false to draw it painted (not a turned prop, or its piece not made yet: past the art
   * budget, it is made on a later frame).
   */
  take(pr: Prop, origin: IsoOrigin): boolean {
    const src = pr.turned;
    const iso = this.iso;
    if (!src || !iso) return false;
    let p = this.pieces.get(src);
    if (p === undefined) {
      // Made under the frame's art budget: a building's faces and rest picture take a few ms, and a mountain edge
      // brings hundreds of stone blocks into view at once.
      if (!canDraw()) return false;
      p = timed(() => (src.kind === 'building' ? this.building(src, origin) : this.block(src, pr, origin)));
      this.pieces.set(src, p);
    }
    if (!p) return false;
    if (!this.seen.has(p)) {
      // Placed at the camera's angle now (its strips made under the budget, or an earlier step turned on exactly);
      // a piece with nothing made yet and no budget left is drawn painted this frame.
      if (p.angle !== iso.angle || p.stepUsed !== this.step || p.pitch !== iso.pitch) {
        if (!canDraw() && !nearestStep(p, iso.angle * 90, iso.pitch)) return false;
        p.angle = iso.angle;
        p.pitch = iso.pitch;
        p.stepUsed = this.step;
        this.place(p, iso, this.step);
      }
      this.seen.add(p);
    }
    return true;
  }

  private building(src: Extract<TurnedSource, { kind: 'building' }>, origin: IsoOrigin): Piece | null {
    const faces = paintedFaces.get(src.img);
    if (!faces?.length || resOf(src.img) !== 1) return null;
    const { st } = src;
    return makePiece(
      faces,
      { img: src.img, ax: src.ax, ay: src.ay },
      (x, y) => [st.i + x, st.j + y],
      src.swap,
      [st.i, st.j, st.i + st.w, st.j + st.h],
      origin,
      true,
      true,
    );
  }

  /** A stone block: a box of its tile, its picture anchored where the prop stands (the tile's north corner). */
  private block(src: Extract<TurnedSource, { kind: 'block' }>, pr: Prop, origin: IsoOrigin): Piece | null {
    if (resOf(pr.img) !== 1) return null;
    const { i, j, h } = src;
    const [sx, sy] = toScreen(i, j, origin);
    return makePiece(boxOf(h), { img: pr.img, ax: sx - pr.x, ay: sy - pr.y }, (x, y) => [i + x, j + y], false, [i, j, i + 1, j + 1], origin, false, false);
  }

  /**
   * The strips to draw this frame in the turned view's rectangle `x0..x1, y0..y1` (world pixels), and
   * the pieces on screen with an upright rest picture.
   */
  strips(x0: number, x1: number, y0: number, y1: number): { strips: TurnedStrip[]; rests: Piece[] } {
    const out: TurnedStrip[] = [];
    const rests: Piece[] = [];
    this.onScreen.length = 0;
    for (const p of this.seen) {
      let on = false;
      for (const st of p.made)
        if (st.x1 >= x0 && st.x0 <= x1 && st.y1 >= y0 && st.y0 <= y1) {
          out.push(st);
          on = true;
        }
      if (on) this.onScreen.push(p);
      if (p.rest) rests.push(p);
    }
    return { strips: out, rests };
  }

  /** Pieces with a strip on screen this frame, and the way toward the camera on the map. */
  private readonly onScreen: Piece[] = [];
  private toward: [number, number] = [1, 1];
  private lastAngle = Number.NaN;
  private lastPitch = 30;

  /**
   * A figure's depth key, set right against the buildings whose strips it overlaps on screen.
   * Strips sort by the footprint's front edge under their own column, so at an odd angle a figure
   * standing just in front of a face could lose to the nearer corner's strip beside it (an arm cut
   * off by the wall), and one behind a building could beat its outer strips (drawn over the side).
   * Seen along the ground toward the camera, a figure whose way back to the camera (or away from
   * it) crosses the footprint is behind (or in front of) the whole building: its key goes under
   * (or over) every strip of it it overlaps. `sx`, `sy` is its foot on screen (world pixels).
   */
  figureDepth(d: number, x: number, y: number, sx: number, sy: number): number {
    if (!this.onScreen.length) return d;
    const [tx, ty] = this.toward;
    const tl = Math.hypot(tx, ty) || 1;
    const [ux, uy] = [tx / tl, ty / tl];
    // Body half width on the map, across the view.
    const W = 0.3;
    const fx0s = sx - 12;
    const fx1s = sx + 12;
    const fy0s = sy - 64;
    for (const p of this.onScreen) {
      const [a0, b0, a1, b1] = p.foot;
      if (x >= a0 && x <= a1 && y >= b0 && y <= b1) continue;
      let lo = Infinity;
      let hi = -Infinity;
      for (const st of p.made)
        if (st.x1 >= fx0s && st.x0 <= fx1s && st.y1 >= fy0s && st.y0 <= sy) {
          if (st.d < lo) lo = st.d;
          if (st.d > hi) hi = st.d;
        }
      if (hi === -Infinity) continue;
      let front = false;
      let behind = false;
      for (const k of [-W, 0, W]) {
        const px = x - uy * k;
        const py = y + ux * k;
        const t = rayBox(px, py, ux, uy, a0, b0, a1, b1);
        if (t === null) continue;
        if (t > 0) behind = true;
        else front = true;
      }
      if (front) d = Math.max(d, hi + 0.01);
      else if (behind) d = Math.min(d, lo - 0.01);
    }
    return d;
  }

  /** The pieces seen this frame (for the fade of the ones in front of the player). */
  all(): Iterable<Piece> {
    return this.seen;
  }

  /** Puts a piece's strips at the camera's exact angle, from its nearest angle step (made if new). */
  private place(p: Piece, iso: IsoCamera, step: number): void {
    const deg = iso.angle * 90;
    // The pitch (0–90°, in half degrees) is part of the step's key: heights change with it.
    const key = Math.round(deg / step) * step * 1000 + Math.round(iso.pitch * 2);
    const [fx0, fy0, fx1, fy1] = p.foot;
    const mx = (fx0 + fx1) / 2;
    const my = (fy0 + fy1) / 2;
    let made = p.steps.get(key);
    let stale = false;
    if (made) {
      p.steps.delete(key);
    } else if (!canDraw() && (made = nearestStep(p, deg, iso.pitch))) {
      // Past the frame's art budget (a turn with many buildings in view): the nearest step made already, turned on
      // exactly to the angle (only its visible faces and strip cuts are a little off), and made properly next frame.
      stale = true;
      p.angle = Number.NaN;
    } else {
      // Made at the step's angle: the camera is set there for the making, then put back.
      const exact = iso.angle;
      const at = Math.round(deg / step) * step;
      made = timed(() => {
        iso.setAngle(at / 90);
        const c = Math.cos(iso.spin);
        const s = Math.sin(iso.spin);
        // The view's elevation follows the pitch (VIEW_Z is the painted 30°'s); above 45° the projection is oblique,
        // so it is the projection's own view direction (lane CAMERA-PITCH, pitchView.ts), not the camera's pitch.
        const tw: V3 = [c + s, c - s, (VIEW_Z * iso.viewTan) / Math.tan(Math.PI / 6)];
        const strips = this.make(p, iso, tw, c >= 0 ? 0 : 1);
        const [cx, cy] = iso.proj(mx, my);
        const out: Made = { strips, cx, cy, cd: iso.depth(mx, my), frontD: p.frontD, deg: at, pitch: iso.pitch };
        iso.setAngle(exact);
        return out;
      });
      if (p.steps.size >= STEPS_KEPT) p.steps.delete(p.steps.keys().next().value!);
    }
    if (!stale) p.steps.set(key, made);
    const [cx, cy] = iso.proj(mx, my);
    const ox = cx - made.cx;
    const oy = cy - made.cy;
    const od = iso.depth(mx, my) - made.cd;
    // The exact turn: ground points turned on about the map's middle by the little past the step.
    const da = ((deg - made.deg) * Math.PI) / 180;
    const [a, b, c, d] = iso.turnPx(da);
    const [mcx, mcy] = iso.centre();
    const m: Affine = [a, b, c, d, mcx - (a * mcx + c * mcy), mcy - (b * mcx + d * mcy)];
    for (const st of made.strips) {
      st.m = m;
      st.d = st.base.d + od;
      st.x0 = st.base.x0 + ox;
      st.x1 = st.base.x1 + ox;
      st.y0 = st.base.y0 + oy;
      st.y1 = st.base.y1 + oy;
    }
    p.frontD = made.frontD + od;
    p.made = made.strips;
  }

  private make(p: Piece, iso: IsoCamera, toward: V3, levelSrc: 0 | 1): TurnedStrip[] {
    const vis = p.faces.filter((f) => (f.level ? f.src === levelSrc : dot(f.n, toward) > 1e-3));
    vis.sort((a, b) => dot(a.c, toward) - dot(b.c, toward));
    const polys = vis.map((f) =>
      f.p.map((q, k) => {
        const [x, y] = iso.proj(q[0], q[1]);
        const z = q[2] * iso.heightK;
        // Screen point (for clipping and bounds), u, v, and its ground point and height (kept
        // apart, so a frame can turn the ground point on by the little past the step exactly).
        return [x, y - z, f.uv[k]![0], f.uv[k]![1], x, y, z];
      }),
    );
    // The footprint's corners at this angle: screen x and depth, for the front edge under a strip.
    const [fx0, fy0, fx1, fy1] = p.foot;
    const fc = [
      [fx0, fy0],
      [fx1, fy0],
      [fx1, fy1],
      [fx0, fy1],
    ].map(([x, y]) => [iso.proj(x!, y!)[0], iso.depth(x!, y!)] as const);
    p.frontD = Math.max(...fc.map((q) => q[1]));
    const front = (X: number): number => {
      const xs = fc.map((q) => q[0]);
      const x = Math.max(Math.min(...xs), Math.min(Math.max(...xs), X));
      let best = -Infinity;
      for (let k = 0; k < 4; k++) {
        const [ax, ad] = fc[k]!;
        const [bx, bd] = fc[(k + 1) % 4]!;
        if ((x < ax && x < bx) || (x > ax && x > bx)) continue;
        const d = bx === ax ? Math.max(ad, bd) : ad + ((bd - ad) * (x - ax)) / (bx - ax);
        if (d > best) best = d;
      }
      return best;
    };
    const tris = (pp: number[][][]): Float32Array => {
      let n = 0;
      for (const q of pp) n += Math.max(0, q.length - 2) * 3;
      const a = new Float32Array(n * TRI);
      let o = 0;
      for (const q of pp)
        for (let k = 1; k + 1 < q.length; k++)
          for (const v of [q[0]!, q[k]!, q[k + 1]!]) {
            a[o++] = v[4]!;
            a[o++] = v[5]!;
            a[o++] = v[6]!;
            a[o++] = v[2]!;
            a[o++] = v[3]!;
          }
      return a;
    };
    const bounds = (pp: number[][][]) => {
      let [a, b, cc, d] = [Infinity, -Infinity, Infinity, -Infinity];
      for (const q of pp)
        for (const v of q) {
          a = Math.min(a, v[0]!);
          b = Math.max(b, v[0]!);
          cc = Math.min(cc, v[1]!);
          d = Math.max(d, v[1]!);
        }
      return [a, b, cc, d] as const;
    };
    if (!polys.length) return [];
    const [bx0, bx1] = bounds(polys);
    if (!p.strips) {
      const [a, b, cc, d] = bounds(polys);
      return [strip(p, Math.max(...fc.map((q) => q[1])), a, b, cc, d, tris(polys))];
    }
    const out: TurnedStrip[] = [];
    for (let sx = Math.floor(bx0 / STRIP) * STRIP; sx < bx1; sx += STRIP) {
      const parts = polys.map((q) => clip(q, sx, sx + STRIP)).filter((q) => q.length >= 3);
      if (!parts.length) continue;
      const [a, b, cc, d] = bounds(parts);
      out.push(strip(p, front(sx + STRIP / 2), a, b, cc, d, tris(parts)));
    }
    return out;
  }

  /** Starts a frame's meshes (each run of strips drawn in one goes into the next pooled mesh). */
  begin(): void {
    this.used = 0;
  }

  /**
   * One mesh of consecutive strips of one texture, at `alpha`, placed by the world picture's
   * transform `m` (art pixels → its canvas) and the frame's offsets.
   */
  mesh(run: readonly TurnedStrip[], alpha: number, m: DOMMatrix, ox: number, oy: number): Mesh | null {
    let n = 0;
    for (const s of run) n += s.tri.length / TRI;
    if (!n) return null;
    const pos = new Float32Array(n * 2);
    const uvs = new Float32Array(n * 2);
    const idx = new Uint32Array(n);
    let k = 0;
    for (const s of run)
      for (let i = 0, t = s.tri, [a, b, c, d, e, f] = s.m; i < t.length; i += TRI) {
        const gx = t[i]!;
        const gy = t[i + 1]!;
        pos[k * 2] = a * gx + c * gy + e;
        pos[k * 2 + 1] = b * gx + d * gy + f - t[i + 2]!;
        uvs[k * 2] = t[i + 3]!;
        uvs[k * 2 + 1] = t[i + 4]!;
        idx[k] = k;
        k++;
      }
    let mesh = this.meshes[this.used];
    if (!mesh) {
      mesh = new Mesh({ geometry: new MeshGeometry({ positions: pos, uvs, indices: idx }), texture: run[0]!.piece.tex });
      this.meshes.push(mesh);
    } else {
      const g = mesh.geometry;
      g.positions = pos;
      g.uvs = uvs;
      g.indices = idx;
      if (mesh.texture !== run[0]!.piece.tex) mesh.texture = run[0]!.piece.tex;
    }
    this.used++;
    mesh.alpha = alpha;
    mesh.setFromMatrix(new Matrix(m.a, m.b, m.c, m.d, m.e + ox * m.a + oy * m.c, m.f + ox * m.b + oy * m.d));
    return mesh;
  }
}

/** The step made already nearest the angle `deg` at this pitch, if any. */
function nearestStep(p: Piece, deg: number, pitch: number): Made | undefined {
  let best: Made | undefined;
  let gap = Infinity;
  for (const m of p.steps.values()) {
    if (m.pitch !== pitch) continue;
    const d = Math.abs(((deg - m.deg + 540) % 360) - 180);
    if (d < gap) {
      gap = d;
      best = m;
    }
  }
  return best;
}

/**
 * Where the line through (x, y) along (dx, dy) meets the box a0..a1 × b0..b1: the signed distance
 * to the nearest crossing (positive ahead, negative behind), or null if it misses.
 */
function rayBox(x: number, y: number, dx: number, dy: number, a0: number, b0: number, a1: number, b1: number): number | null {
  let t0 = -Infinity;
  let t1 = Infinity;
  for (const [o, v, lo, hi] of [
    [x, dx, a0, a1],
    [y, dy, b0, b1],
  ] as const) {
    if (Math.abs(v) < 1e-9) {
      if (o < lo || o > hi) return null;
      continue;
    }
    let u0 = (lo - o) / v;
    let u1 = (hi - o) / v;
    if (u0 > u1) [u0, u1] = [u1, u0];
    t0 = Math.max(t0, u0);
    t1 = Math.min(t1, u1);
    if (t0 > t1) return null;
  }
  return t0 > 0 ? t0 : t1 < 0 ? t1 : 0;
}

function strip(piece: Piece, d: number, x0: number, x1: number, y0: number, y1: number, tri: Float32Array): TurnedStrip {
  return { piece, d, x0, x1, y0, y1, tri, m: [1, 0, 0, 1, 0, 0], peek: false, base: { d, x0, x1, y0, y1 } };
}
